import { useEffect, useRef, useState } from "react";
import { Spinner } from "@/components/common/Spinner";
import type { TokenValidationState } from "@/stores/onboardingStore";
import { AnimatedCheckmark } from "./AnimatedCheckmark";

interface TokenInputProps {
  value: string;
  onChange: (v: string) => void;
  /** Called after a debounce once the value has stabilized. */
  onSettled: () => void;
  validation: TokenValidationState;
  /** Debounce ms before calling onSettled (after last keystroke). */
  debounceMs?: number;
  /** Locked (don't allow edits — usually after verified success). */
  locked?: boolean;
  /** Placeholder shown when empty. */
  placeholder?: string;
}

/**
 * Token paste-back field with staged validation states.
 *
 * While the user types, we show a neutral ring. Once typing settles
 * (after ``debounceMs`` of no input), we fire ``onSettled`` which the
 * parent uses to run the real verification. The field's ring and helper
 * text reflect the returned state:
 *
 *  idle          — no ring tint, no helper text
 *  typing        — jove ring, "Keep typing…"
 *  shape_invalid — hydra ring + message
 *  verifying     — oxy ring + spinner
 *  verified      — oxy solid + checkmark, locked
 *  failed        — hydra ring + message
 */
export function TokenInput({
  value,
  onChange,
  onSettled,
  validation,
  debounceMs = 650,
  locked = false,
  placeholder = "sk-or-…",
}: TokenInputProps) {
  const [focused, setFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const settleTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!value.trim()) return;
    if (settleTimer.current) window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => {
      onSettled();
    }, debounceMs);
    return () => {
      if (settleTimer.current) window.clearTimeout(settleTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Subtle shake on failure (triggered by a key change on the container)
  const shakeKey =
    validation.kind === "failed" || validation.kind === "shape_invalid"
      ? validation.message
      : "";

  const ringClass =
    validation.kind === "verified"
      ? "border-oxy/80 ring-4 ring-oxy/15"
      : validation.kind === "failed" || validation.kind === "shape_invalid"
        ? "border-hydra/80 ring-4 ring-hydra/15"
        : validation.kind === "verifying"
          ? "border-oxy/60 ring-4 ring-oxy/10"
          : focused
            ? "border-jove-60 ring-4 ring-jove-50"
            : "border-space-30";

  return (
    <div className="w-full">
      <div
        key={shakeKey}
        className={`relative rounded-xl border bg-surface transition-all duration-200 ease-out ${ringClass}
          ${shakeKey ? "onb-shake" : ""}`}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={locked}
          rows={3}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          placeholder={placeholder}
          className="block w-full resize-none rounded-xl bg-transparent px-4 py-3.5 pr-12 font-mono text-[14px] leading-relaxed text-space placeholder:text-space-40 focus:outline-none disabled:text-space-50"
          aria-label="Paste your API key"
        />

        {/* Status glyph — absolute top-right */}
        <div className="pointer-events-none absolute right-3 top-3.5 flex h-6 w-6 items-center justify-center">
          {validation.kind === "verifying" && <Spinner size="sm" className="text-oxy" />}
          {validation.kind === "verified" && (
            <AnimatedCheckmark size={22} tone="oxy" />
          )}
          {(validation.kind === "failed" || validation.kind === "shape_invalid") && (
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5 text-hydra"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" opacity="0.4" />
              <path d="M12 8v5M12 16.2v.1" />
            </svg>
          )}
        </div>
      </div>

      {/* Helper row */}
      <div className="mt-2 min-h-[18px] text-[12px] font-medium leading-snug tracking-[0.04em]">
        {validation.kind === "typing" && (
          <span className="text-space-50">Keep typing — I'll verify when you're done.</span>
        )}
        {validation.kind === "verifying" && (
          <span className="text-space-50">Verifying your key…</span>
        )}
        {validation.kind === "shape_invalid" && (
          <span className="text-hydra">{validation.message}</span>
        )}
        {validation.kind === "failed" && (
          <span className="text-hydra">{validation.message}</span>
        )}
        {validation.kind === "verified" && (
          <span className="text-oxy font-medium">{validation.message}</span>
        )}
      </div>
    </div>
  );
}
